export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      ...rest,
      headers: { ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...headers },
      body: json !== undefined ? JSON.stringify(json) : rest.body,
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'PRISM is unreachable. Check your connection; we will keep retrying.');
  }
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? `HTTP_${res.status}`, err?.message ?? `Request failed (${res.status})`, err?.details);
  }
  return body as T;
}

/** The message of a caught error, as toasts and banners show it. */
export const errorMessage = (err: unknown): string => (err as Error).message;

/** Like errorMessage, but a validation error lists its issues (field: problem; …). */
export const errorDetail = (err: unknown): string => {
  const e = err as ApiError;
  const issues = (e.details?.issues as Array<{ path: string[]; message: string }> | undefined)?.map((i) => `${i.path.join('.')}: ${i.message}`);
  return issues?.join('; ') ?? e.message;
};
